import { NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { ELEKTRO_ABSCHNITTE } from './abschnitte';
import { useSchmaleLeiste } from '../../rahmen/schmal';

/**
 * Der Rahmen um alle Elektroseiten — links die Gliederung, rechts die Seite.
 *
 * --- Warum ein Rahmen und keine Reiterzeile -------------------------------
 *
 * Dasselbe Muster wie bei Heizung/Kälte: links der Baum, rechts der gewählte
 * Knoten. Eine waagerechte Reihe wächst mit jedem Paket in die Breite und ist
 * irgendwann nicht mehr lesbar; eine senkrechte Gliederung wächst nach unten
 * und bleibt es. Vor allem aber trägt sie Abschnitte: acht gleichrangige
 * Knöpfe sagen nicht, dass der Melderbedarf mit den Meldelinien zu tun hat
 * und mit der Kabeltrasse nicht.
 *
 * --- Warum eine Layoutroute -----------------------------------------------
 *
 * Der Rahmen hängt als elternlose Route über den bestehenden Elektrorouten
 * (React Router: Route ohne `path`, Kinder unverändert). Dadurch ändert sich
 * KEIN Pfad — Deep Links und Lesezeichen gelten weiter, und die Seiten selbst
 * wissen nichts vom Rahmen. Wer eine Seite ergänzt, trägt sie in
 * `abschnitte.ts` ein; hier ist nichts zu tun.
 *
 * --- Warum der Kontext durchgereicht wird ---------------------------------
 *
 * Eine Layoutroute ist im Router eine ECHTE Ebene, auch wenn sie keinen Pfad
 * trägt. `useOutletContext` reicht immer nur eine Ebene weit: Was der
 * Projektrahmen an seinen Outlet gibt, landet hier - und nicht mehr bei den
 * Seiten darunter. Ohne die Weitergabe bekommt jede Elektroseite `undefined`
 * und stirbt beim Auspacken von `api`; die Seite bleibt weiss. Genau das ist
 * am 17.09.2026 in der Produktion passiert, und es fiel in keinem Test auf,
 * weil das Testtor das Frontend nicht ausführt.
 *
 * Der Rahmen kennt den Inhalt des Kontexts absichtlich nicht - er reicht
 * ihn unverändert weiter. So bleibt er auch dann richtig, wenn der
 * Projektrahmen später ein Feld ergänzt.
 *
 * --- Warum der Baum links verschwinden kann (SL7) -------------------------
 *
 * Seit die Seitenleiste nach Gewerken gegliedert ist, trägt sie unter
 * „Elektro" dieselben fünf Abschnitte und zwölf Seiten - aus derselben
 * Quelle (`abschnitte.ts`). Bei breiter, ausgeklappter Leiste stand die
 * Gliederung damit ZWEIMAL im Bild, nebeneinander, mit denselben Wörtern.
 *
 * Ist die Leiste dagegen schmal (von Hand eingeklappt oder Fenster unter
 * 1280 px), zeigt sie nur noch Sinnbilder - dann ist dieser Baum der
 * einzige Weg zu den Abschnitten, und er bleibt. Die Frage „ist die Leiste
 * schmal?" wird NICHT hier zum zweiten Mal beantwortet, sondern in
 * `rahmen/schmal.ts` gelesen.
 *
 * Der `Outlet` und seine Kontextweitergabe sind davon unberührt: Ob der
 * Baum steht oder nicht, ändert an der Router-Ebene nichts.
 */
export function ElektroRahmen() {
  const { id } = useParams();
  const kontext = useOutletContext();
  const schmaleLeiste = useSchmaleLeiste();

  return (
    <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
      {schmaleLeiste && (
        <nav className="baum" aria-label="Gliederung Elektro">
          {ELEKTRO_ABSCHNITTE.map((abschnitt) => (
            <div key={abschnitt.name}>
              <div className="baum-abschnitt">{abschnitt.name}</div>
              {abschnitt.eintraege.map((eintrag) => (
                <NavLink
                  key={eintrag.pfad}
                  to={`/projekte/${id}/${eintrag.pfad}`}
                  end
                  title={eintrag.hinweis}
                  className={({ isActive }) =>
                    `baum-zeile${isActive ? ' aktiv' : ''}`}
                >
                  {eintrag.name}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      )}

      {/* Die Seite selbst. Sie bringt ihre eigene Karte mit, deshalb hier
          nur Platz und Polsterung - sonst säße eine Karte in einer Karte. */}
      <div style={{ flex: 1, minWidth: 0, padding: 16 }}>
        <Outlet context={kontext} />
      </div>
    </div>
  );
}
